/**
 * useRunModuleOutputs — React Query hook for per-module structured outputs.
 *
 * Fetches module outputs once the run reaches a terminal status.
 * No polling — outputs are written during EVALUATING and don't change after.
 *
 * Demo mode: returns static mock outputs for the CERTIFIED demo run.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchRunModuleOutputs } from '../api/environmentRuns';
import { isDemoModeEnabled } from '../demo/demoMode';
import type { EnvironmentRunModuleOutput, EnvironmentRunStatus } from '../types/environmentRun';
import { ENV_TERMINAL_STATUSES } from '../types/environmentRun';

// ── Demo data ────────────────────────────────────────────────────────────

const DEMO_OUTPUTS: Record<string, EnvironmentRunModuleOutput[]> = {
  env_run_001: [
    {
      output_id: 'out_001a',
      run_id: 'env_run_001',
      module_id: 'forge-1',
      module_kind: 'proposal_source',
      output_type: 'forge_intake',
      output_json: {
        feed_id: 'usgs_reviewed',
        source_id: 'usgs_reviewed',
        domain: 'seismic',
        ir_version: '1.0',
        status: 'PROCESSED',
        proposals_received: 5,
        proposals_admitted: 3,
        proposals_rejected: 1,
        proposals_skipped: 1,
        dispositions_count: 4,
      },
      created_at: '2026-03-21T14:23:00Z',
    },
    {
      output_id: 'out_001b',
      run_id: 'env_run_001',
      module_id: 'tremor-1',
      module_kind: 'theatre_construct_draft',
      output_type: 'theatre_evaluation',
      output_json: {
        construct_slug: 'tremor-seismic-v3',
        brier_score: 0.142,
        composite_score: 0.75,
        checks_executed: 4,
        checks_passed: 3,
        checks_failed: 1,
        has_critical_failures: false,
        verification_tier: 'EVALUATED',
        replay_count: 2,
        replay_verified: true,
        is_primary: true,
      },
      created_at: '2026-03-21T14:25:00Z',
    },
    {
      output_id: 'out_001c',
      run_id: 'env_run_001',
      module_id: 'scenario-1',
      module_kind: 'scenario_pack',
      output_type: 'scenario_pack_evaluation',
      output_json: {
        template_id: 'tremor_seismic_crisis_v1',
        template_name: 'Seismic Crisis Scenario',
        run_mode: 'EVALUATION',
        environment_seed: 47,
        status: 'COMPLETED',
        total_reward: 2.35,
        checkpoints_resolved: 4,
        checkpoints_total: 5,
        scenario_run_id: 'sr_demo_001',
      },
      created_at: '2026-03-21T14:26:00Z',
    },
    {
      output_id: 'out_001d',
      run_id: 'env_run_001',
      module_id: 'agent-1',
      module_kind: 'agent_actor',
      output_type: 'agent_readiness_evaluation',
      output_json: {
        agent_id: 'agent_alpha_001',
        agent_name: 'Agent-α',
        archetype: 'SHARK',
        tier: 1,
        level: 3,
        is_alive: true,
        death_cause: null,
        sanity: 87,
        max_sanity: 100,
        sanity_ok: true,
        deployment_ready: true,
        active_deployments: 1,
        total_pnl_usd: 142.50,
        win_rate: 0.62,
        trades_count: 8,
      },
      created_at: '2026-03-21T14:27:00Z',
    },
  ],
};

const EMPTY_OUTPUTS: EnvironmentRunModuleOutput[] = [];

async function fetchWithDemoFallback(
  runId: string,
  forceDemo: boolean,
): Promise<EnvironmentRunModuleOutput[]> {
  if (forceDemo) return DEMO_OUTPUTS[runId] ?? [];
  try {
    return await fetchRunModuleOutputs(runId);
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status && status >= 400 && status < 500) throw err;
    return DEMO_OUTPUTS[runId] ?? [];
  }
}

export function useRunModuleOutputs(
  runId: string | null,
  runStatus: EnvironmentRunStatus | null,
) {
  const isDemo = isDemoModeEnabled();
  const isTerminal = runStatus ? ENV_TERMINAL_STATUSES.includes(runStatus) : false;
  // Active runs produce outputs progressively during EVALUATING/SETTLING/CERTIFYING.
  // Fetch and poll during active runs; cache permanently once terminal.
  const isActive = runStatus != null && !isTerminal && runStatus !== 'QUEUED';

  const query = useQuery({
    queryKey: ['environmentRunModuleOutputs', runId, isDemo],
    queryFn: () => fetchWithDemoFallback(runId!, isDemo),
    enabled: !!runId && (isTerminal || isActive),
    staleTime: isTerminal ? Infinity : 5000,
    refetchInterval: isActive ? 5000 : false,
  });

  return {
    outputs: query.data ?? EMPTY_OUTPUTS,
    isLoading: query.isLoading,
    error: query.error,
  };
}
